import { UseFormSetValue } from 'react-hook-form'
import {
  BeneficiaryDetails,
  BeneficiaryDetailsCurrency,
  BeneficiaryEntityType,
  BeneficiaryTransferDestination,
  BeneficiaryType,
  PaymentMethods,
  ValidateIbanResponse,
} from 'types/beneficiaries'
import {
  AccountHolderName,
  Contact,
  ContactEntity,
  ContactEntityCompany,
  ContactEntityIndividual,
  ContactInstrument,
  CreateContactBankAccountRequest,
  InstrumentType,
} from 'types/contacts'
import { ConfirmPayeeBeneficiaryParams } from 'types/payoutsV2'
import {
  ContactFiatInstrumentFormData,
  ContactFiatInstrumentFormKeys,
} from '../schemas/contactFiatInstrumentSchema'
import { ContactFormData } from '../schemas/contactSchema'
import { FieldsFetchedFromIbanValidation, Step, Steps } from '../types/fiatInstruments'
import {
  BANK_ADDRESS_FIELDS,
  BASIC_BANK_ACCOUNT_FIELDS,
  BASIC_FIELDS,
  BENEFICIARY_ADDRESS_FIELDS,
  CURRENCY_DETAILS_FIELDS,
  EEA_COUNTRY_CODES,
  INTERMEDIARY_BANK_FIELDS,
} from './constants.fiatInstruments'
import { isContactEntityCompany, isContactEntityIndividual } from './utils'

export const isBeneficiaryAddressFieldsRequired = ({
  field,
  formFields,
}: {
  formFields: ContactFiatInstrumentFormData
  field: string
}) => {
  const {
    currency,
    beneficiaryAddressLine1,
    beneficiaryAddressLine2,
    beneficiaryCity,
    beneficiaryPostCode,
    beneficiaryRegion,
    beneficiaryCountryCode,
    paymentMethods,
    accountNumber,
  } = formFields || {}
  const isUsd = currency === BeneficiaryDetailsCurrency.USD
  const isSwift = paymentMethods?.includes(PaymentMethods.SWIFT)
  const isLocal = !!paymentMethods?.length && !isSwift
  const isAddressLine2Field = field === 'beneficiaryAddressLine2'
  const isCountryField = field === 'beneficiaryCountryCode'
  const isEurAndCountryIsNonEea =
    currency === BeneficiaryDetailsCurrency.EUR &&
    !EEA_COUNTRY_CODES.includes(accountNumber?.slice(0, 2).toUpperCase() ?? '')

  if (isAddressLine2Field) {
    return false
  }

  if ((isUsd && isLocal) || isSwift || isCountryField || isEurAndCountryIsNonEea) {
    return true
  }

  return (
    beneficiaryAddressLine1 ||
    beneficiaryAddressLine2 ||
    beneficiaryCity ||
    beneficiaryPostCode ||
    beneficiaryRegion ||
    beneficiaryCountryCode
  )
}

export const isAddressFieldsRequired = ({
  currency,
  isSwiftPaymentMethodSelected,
}: {
  currency?: string
  isSwiftPaymentMethodSelected?: boolean
}) => {
  const isLocalUSD = !isSwiftPaymentMethodSelected && currency === BeneficiaryDetailsCurrency.USD

  return isSwiftPaymentMethodSelected || isLocalUSD
}

export const getFieldsToValidate = (
  step: Step,
  formValues: ContactFiatInstrumentFormData
): ContactFiatInstrumentFormKeys[] => {
  const { paymentMethods, isIntermediaryBankDetailsRequired } = formValues || {}
  const isInternational = paymentMethods?.includes(PaymentMethods.SWIFT)

  switch (step) {
    case Steps.CURRENCY_DETAILS:
      return CURRENCY_DETAILS_FIELDS
    case Steps.BENEFICIARY_DETAILS:
      return [...BASIC_FIELDS, ...BENEFICIARY_ADDRESS_FIELDS]
    case Steps.BANK_DETAILS:
      return isInternational
        ? [
            ...BASIC_BANK_ACCOUNT_FIELDS,
            ...BANK_ADDRESS_FIELDS,
            ...(isIntermediaryBankDetailsRequired ? INTERMEDIARY_BANK_FIELDS : []),
          ]
        : [...BASIC_BANK_ACCOUNT_FIELDS]
    default:
      return []
  }
}

export const getIsValidIban = ({
  currency,
  ibanBankDetails,
}: {
  currency?: string
  ibanBankDetails?: ValidateIbanResponse
  ibanSupportedPaymentMethods?: string[]
}) => {
  switch (currency) {
    case 'AED':
      return !!ibanBankDetails?.bankAccount?.bankCode && !ibanBankDetails?.validationFailed?.length
    case 'EUR':
    case 'GBP':
    case 'USD':
      return (
        (ibanBankDetails?.supportedPaymentMethods?.includes('SEPA_CT') ||
          ibanBankDetails?.supportedPaymentMethods?.includes('SEPA_INST')) &&
        !ibanBankDetails?.validationFailed?.length
      )
    default:
      return false
  }
}

export const cleanIbanValidationResponse = (
  bankDetails: ValidateIbanResponse
): ValidateIbanResponse => {
  return {
    bankAccount: {
      type: bankDetails?.bankAccount?.type,
      bankName: bankDetails?.bankAccount?.bankName?.trim(),
      accountNumber: bankDetails?.bankAccount?.accountNumber?.trim(),
      bankCode: bankDetails?.bankAccount?.bankCode?.trim(),
      bankAddress: {
        addressLine1: bankDetails?.bankAccount?.bankAddress?.addressLine1?.trim(),
        addressLine2: bankDetails?.bankAccount?.bankAddress?.addressLine2?.trim(),
        city: bankDetails?.bankAccount?.bankAddress?.city?.trim(),
        region: bankDetails?.bankAccount?.bankAddress?.region?.trim(),
        postCode: bankDetails?.bankAccount?.bankAddress?.postCode?.trim(),
        countryCode: bankDetails?.bankAccount?.bankAddress?.countryCode?.trim(),
      },
    },
    validationFailed: bankDetails?.validationFailed,
    supportedPaymentMethods: bankDetails?.supportedPaymentMethods,
  }
}

export const setFieldsFetchedFromIbanValidation = (
  ibanValidationResponse: ValidateIbanResponse
): FieldsFetchedFromIbanValidation => {
  const cleanDetail = cleanIbanValidationResponse(ibanValidationResponse)

  if (!cleanDetail?.validationFailed?.length) {
    const { bankCode, bankName, bankAddress, accountNumber } = cleanDetail?.bankAccount || {}

    return {
      ...(accountNumber && {
        accountNumber: accountNumber,
      }),
      ...(bankCode && {
        bankCode: bankCode,
      }),
      ...(bankName && {
        bankName: bankName,
      }),
      ...(bankAddress?.addressLine1 && {
        bankAddressLine1: bankAddress?.addressLine1,
      }),
      ...(bankAddress?.addressLine2 && {
        bankAddressLine2: bankAddress?.addressLine2,
      }),
      ...(bankAddress?.city && {
        bankCity: bankAddress?.city,
      }),
      ...(bankAddress?.postCode && {
        bankPostCode: bankAddress?.postCode,
      }),
      ...(bankAddress?.region && {
        bankRegion: bankAddress?.region,
      }),
    }
  }
}

export const resetBankAddress = (
  setValue: UseFormSetValue<ContactFiatInstrumentFormData>,
  prefix: string
) => {
  setValue(`${prefix}Name` as keyof ContactFiatInstrumentFormData, '')
  setValue(`${prefix}AddressLine1` as keyof ContactFiatInstrumentFormData, '')
  setValue(`${prefix}AddressLine2` as keyof ContactFiatInstrumentFormData, '')
  setValue(`${prefix}City` as keyof ContactFiatInstrumentFormData, '')
  setValue(`${prefix}Region` as keyof ContactFiatInstrumentFormData, '')
  setValue(`${prefix}PostCode` as keyof ContactFiatInstrumentFormData, '')
}

const mapBeneficiaryEntityType = (beneficiaryEntityType?: string) => {
  if (beneficiaryEntityType === BeneficiaryEntityType.BUSINESS) {
    return ContactEntity.COMPANY
  }
  if (beneficiaryEntityType === BeneficiaryEntityType.INDIVIDUAL) {
    return ContactEntity.INDIVIDUAL
  }
  return ContactEntity.COMPANY
}

const mapIndividualBeneficiaryToForm = (
  beneficiary: BeneficiaryDetails
): Partial<ContactFormData> => {
  // Convert dateOfBirth for SingleDate component
  const formattedDateOfBirth = beneficiary?.dateOfBirth
    ? beneficiary.dateOfBirth.replace(/\//g, ',')
    : ''

  return {
    entityType: ContactEntity?.INDIVIDUAL,
    firstName: beneficiary?.firstName || '',
    lastName: beneficiary?.lastName || '',
    contactDescription: beneficiary?.alias || '',
    addressLine1: beneficiary?.address?.addressLine1 || '',
    addressLine2: beneficiary?.address?.addressLine2 || '',
    city: beneficiary?.address?.city || '',
    postCode: beneficiary?.address?.postCode || '',
    region: beneficiary?.address?.region || '',
    countryOfResidence: beneficiary?.addressCountry || beneficiary?.address?.country || '',
    dateOfBirth: formattedDateOfBirth,
    isSelfOwned: beneficiary?.beneficiaryType === BeneficiaryType.FIRST_PARTY,
  }
}

const mapCompanyBeneficiaryToForm = (beneficiary: BeneficiaryDetails): Partial<ContactFormData> => {
  return {
    entityType: ContactEntity.COMPANY,
    companyName: beneficiary?.businessName || '',
    contactDescription: beneficiary?.alias || '',
    companyRegistrationCountry: beneficiary?.addressCountry || beneficiary?.address?.country || '',
    addressLine1: beneficiary?.address?.addressLine1 || '',
    addressLine2: beneficiary?.address?.addressLine2 || '',
    city: beneficiary?.address?.city || '',
    postCode: beneficiary?.address?.postCode || '',
    region: beneficiary?.address?.region || '',
    isSelfOwned: beneficiary?.beneficiaryType === BeneficiaryType.FIRST_PARTY,
  }
}

const mapBeneficiaryToContactForm = (beneficiary: BeneficiaryDetails): Partial<ContactFormData> => {
  const entityType = mapBeneficiaryEntityType(beneficiary.entityType)

  if (entityType === ContactEntity.COMPANY) {
    return mapCompanyBeneficiaryToForm(beneficiary)
  }

  return mapIndividualBeneficiaryToForm(beneficiary)
}

export const populateContactFormWithBeneficiaryDetails = (
  beneficiary: BeneficiaryDetails,
  setValue: UseFormSetValue<ContactFormData>
) => {
  const formData = mapBeneficiaryToContactForm(beneficiary)

  Object.entries(formData).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      setValue(key as keyof ContactFormData, value, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }
  })
}

const mapBeneficiaryPaymentMethods = (beneficiary: BeneficiaryDetails): string[] => {
  switch (beneficiary?.currency) {
    case BeneficiaryDetailsCurrency.USD:
      return [beneficiary?.paymentMethod]
    case BeneficiaryDetailsCurrency.EUR:
      return beneficiary?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
        ? [PaymentMethods.SWIFT]
        : [PaymentMethods.SEPA, PaymentMethods.SEPA_INSTANT]
    case BeneficiaryDetailsCurrency.GBP:
      return beneficiary?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
        ? [PaymentMethods.SWIFT]
        : [PaymentMethods.FASTER_PAYMENT]
    case BeneficiaryDetailsCurrency.AED:
      return [PaymentMethods.SWIFT]
    case BeneficiaryDetailsCurrency.ZAR:
      return []
    default:
      return []
  }
}

const mapBeneficiaryToFiatInstrumentForm = (
  beneficiary: BeneficiaryDetails
): Partial<ContactFiatInstrumentFormData> => {
  const accountDetails = beneficiary?.fiat
  const bankAddress = accountDetails?.bankAddress
  const intermediaryBank = accountDetails?.intermediaryBank

  const isLocal = beneficiary?.transferDestination === BeneficiaryTransferDestination.LOCAL
  const isInternational =
    beneficiary?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
  const isUSD = beneficiary?.currency === BeneficiaryDetailsCurrency.USD
  const isZAR = beneficiary?.currency === BeneficiaryDetailsCurrency.ZAR

  const paymentMethods = mapBeneficiaryPaymentMethods(beneficiary)

  const contactBankAccount: Partial<ContactFiatInstrumentFormData> = {
    // Basic beneficiary details
    transferDestination: beneficiary?.transferDestination || '',
    paymentMethods,
    entityType: beneficiary?.entityType || '',
    description: beneficiary?.alias || '',
    currency: beneficiary?.currency || '',
    // Account holder details
    firstName: beneficiary?.firstName || '',
    lastName: beneficiary?.lastName || '',
    businessName: beneficiary?.businessName || '',
    // Bank account details
    accountNumber: accountDetails?.accountNumber || '',
    accountType: accountDetails?.accountType || '',
    bankName: accountDetails?.bankName || '',
    bankCode: accountDetails?.code || '',
    beneficiaryAddressLine1: beneficiary?.address?.addressLine1 || '',
    beneficiaryAddressLine2: beneficiary?.address?.addressLine2 || '',
    beneficiaryCity: beneficiary?.address?.city || '',
    beneficiaryRegion: beneficiary?.address?.region || '',
    beneficiaryPostCode: beneficiary?.address?.postCode,
    beneficiaryCountryCode: beneficiary?.addressCountry || '',
  }

  // Handle bankCode and routingNumber based on transfer type and currency
  if (isLocal) {
    // USD local needs account confirmation
    if (isUSD) {
      contactBankAccount.accountNumberConfirm = accountDetails?.accountNumber || ''
    }

    // ZAR uses bankNameCode for bank selection
    if (isZAR) {
      contactBankAccount.bankNameCode = accountDetails?.code || ''
    }
  } else if (isInternational) {
    // For international beneficiaries, distinguish between BIC and routing number
    if (beneficiary?.bankTypeIdentifier === 'SWIFT') {
      contactBankAccount.bankCode = accountDetails?.code || '' // BIC/SWIFT code
    } else if (beneficiary?.bankTypeIdentifier === 'ABA' && isUSD) {
      contactBankAccount.routingNumber = accountDetails?.code || '' // US routing number
    } else {
      contactBankAccount.bankCode = accountDetails?.code || ''
    }
  }

  if (bankAddress) {
    contactBankAccount.bankAddressLine1 = bankAddress.addressLine1 || ''
    contactBankAccount.bankAddressLine2 = bankAddress.addressLine2 ?? ''
    contactBankAccount.bankCity = bankAddress.city || ''
    contactBankAccount.bankRegion = bankAddress.region || ''
    contactBankAccount.bankPostCode = bankAddress.postCode || ''
    contactBankAccount.bankCountryCode = bankAddress.country || ''
    contactBankAccount.bankCountryName = bankAddress.country || ''
  }

  // Add intermediary bank details if present
  if (intermediaryBank && isInternational) {
    return {
      ...contactBankAccount,
      ...(intermediaryBank.code && {
        intermediaryBankCode: intermediaryBank.code,
      }),
      ...(intermediaryBank?.nid && {
        intermediaryBankRoutingNumber: intermediaryBank.nid,
      }),
      ...(intermediaryBank.bankName && {
        intermediaryBankName: intermediaryBank.bankName,
      }),
      ...(intermediaryBank.bankAddress?.addressLine1 && {
        intermediaryBankAddressLine1: intermediaryBank.bankAddress.addressLine1,
      }),
      ...(intermediaryBank.bankAddress?.addressLine2 && {
        intermediaryBankAddressLine2: intermediaryBank.bankAddress.addressLine2,
      }),
      ...(intermediaryBank.bankAddress?.city && {
        intermediaryBankCity: intermediaryBank.bankAddress.city,
      }),
      ...(intermediaryBank.bankAddress?.region && {
        intermediaryBankRegion: intermediaryBank.bankAddress.region,
      }),
      ...(intermediaryBank.bankAddress?.postCode && {
        intermediaryBankPostCode: intermediaryBank.bankAddress.postCode,
      }),
      ...(intermediaryBank.bankAddress?.country && {
        intermediaryBankCountryCode: intermediaryBank.bankAddress.country,
      }),
    }
  }

  return contactBankAccount
}

export const populateInstrumentFormWithBeneficiaryDetails = (
  beneficiary: BeneficiaryDetails,
  setValue: UseFormSetValue<ContactFiatInstrumentFormData>
) => {
  const formData = mapBeneficiaryToFiatInstrumentForm(beneficiary)

  Object.entries(formData).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      setValue(key as keyof ContactFiatInstrumentFormData, value, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }
  })
}

export const mapFormDataToBeneficiaryDetails = (
  contact: Contact,
  formData: ContactFiatInstrumentFormData
): BeneficiaryDetails => {
  const { entity } = contact || ({} as Contact)
  const isCompany = isContactEntityCompany(entity)
  const isIndividual = isContactEntityIndividual(entity)

  return {
    ...formData,
    currency: formData?.currency as BeneficiaryDetailsCurrency,
    entityType: formData?.entityType as BeneficiaryEntityType,
    transferDestination: BeneficiaryTransferDestination.LOCAL,
    addressCountry: formData?.beneficiaryCountryCode || '',
    fiat: {
      accountNumber: formData?.accountNumber || '',
      code: formData?.bankCode || '',
      bankName: formData?.bankName || '',
    },
    ...(isCompany && {
      businessName: (contact?.entity as ContactEntityCompany)?.legalName || '',
    }),
    ...(isIndividual && {
      firstName: (contact?.entity as ContactEntityIndividual)?.firstName || '',
      lastName: (contact?.entity as ContactEntityIndividual)?.lastName || '',
    }),
  }
}

export const getConfirmPayeeBeneficiaryParams = (
  beneficiary: BeneficiaryDetails
): ConfirmPayeeBeneficiaryParams => {
  return {
    ...(beneficiary?.entityType === BeneficiaryEntityType.INDIVIDUAL && {
      firstName: beneficiary?.firstName,
      lastName: beneficiary?.lastName,
    }),
    ...(beneficiary?.entityType === BeneficiaryEntityType.BUSINESS && {
      businessName: beneficiary?.businessName,
    }),
    accountNumber: beneficiary?.fiat?.accountNumber,
    bankCode: beneficiary?.fiat?.code,
    currency: beneficiary?.currency,
    transferDestination: beneficiary?.transferDestination,
  }
}

export const getPaymentMethods = (formValues: ContactFiatInstrumentFormData) => {
  const isSwiftPaymentMethodSelected = formValues?.paymentMethods?.includes(PaymentMethods.SWIFT)
  const isEurBankAccount = formValues?.currency === BeneficiaryDetailsCurrency.EUR
  const isLocalBankAccount = !!formValues?.paymentMethods?.length && !isSwiftPaymentMethodSelected

  if (isEurBankAccount) {
    return isLocalBankAccount
      ? [
          PaymentMethods.SEPA,
          PaymentMethods.SEPA_INSTANT,
          ...(isSwiftPaymentMethodSelected ? [PaymentMethods.SWIFT] : []),
        ]
      : formValues?.paymentMethods
  }

  return formValues?.paymentMethods ?? []
}

export const getRequestBankPayload = (formValues: ContactFiatInstrumentFormData) => {
  const {
    bankName,
    bankCode,
    routingNumber,
    bankAddressLine1,
    bankAddressLine2,
    bankCity,
    bankRegion,
    bankPostCode,
    bankCountryCode,
    currency,
  } = formValues ?? {}

  const isUSD = currency === BeneficiaryDetailsCurrency.USD

  return {
    ...(bankName && { name: bankName }),
    ...(isUSD
      ? { swiftCode: bankCode, identificationCode: routingNumber }
      : { swiftCode: bankCode }),
    address: {
      ...(bankAddressLine1 && { addressLine1: bankAddressLine1 }),
      ...(bankAddressLine2 && { addressLine2: bankAddressLine2 }),
      ...(bankRegion && { region: bankRegion }),
      ...(bankCity && { city: bankCity }),
      ...(bankPostCode && { postCode: bankPostCode }),
      ...(bankCountryCode && { country: bankCountryCode }),
    },
  }
}

export const getRequestIntermediaryBankPayload = (formValues: ContactFiatInstrumentFormData) => {
  const {
    currency,
    paymentMethods,
    intermediaryBankCode,
    intermediaryBankName,
    intermediaryBankAddressLine1,
    intermediaryBankAddressLine2,
    intermediaryBankCity,
    intermediaryBankRegion,
    intermediaryBankPostCode,
    intermediaryBankCountryCode,
    intermediaryBankRoutingNumber,
  } = formValues ?? {}
  const isSWIFT = paymentMethods?.includes(PaymentMethods.SWIFT)
  const hasIntermediaryBank = isSWIFT && intermediaryBankCode
  const isUSD = currency === BeneficiaryDetailsCurrency.USD

  if (!hasIntermediaryBank) return

  return [
    {
      name: intermediaryBankName,
      ...(isUSD
        ? { swiftCode: intermediaryBankCode, identificationCode: intermediaryBankRoutingNumber }
        : { swiftCode: intermediaryBankCode }),
      address: {
        ...(intermediaryBankAddressLine1 && { addressLine1: intermediaryBankAddressLine1 }),
        ...(intermediaryBankAddressLine2 && { addressLine2: intermediaryBankAddressLine2 }),
        ...(intermediaryBankCity && { city: intermediaryBankCity }),
        ...(intermediaryBankRegion && { region: intermediaryBankRegion }),
        ...(intermediaryBankPostCode && { postCode: intermediaryBankPostCode }),
        ...(intermediaryBankCountryCode && { country: intermediaryBankCountryCode }),
      },
    },
  ]
}

export const getRequestPayload = (
  contact: Contact,
  formValues: ContactFiatInstrumentFormData
): CreateContactBankAccountRequest => {
  const isBusinessContact = contact?.entity?.type === ContactEntity.COMPANY

  const {
    currency,
    description,
    transferDestination,
    accountNumber,
    beneficiaryAddressLine1,
    beneficiaryAddressLine2,
    beneficiaryRegion,
    beneficiaryCity,
    beneficiaryCountryCode,
    beneficiaryPostCode,
  } = formValues ?? {}

  const bankDetails = getRequestBankPayload(formValues)
  const intermediaryBankDetails = getRequestIntermediaryBankPayload(formValues)
  const accountHolderName: AccountHolderName = isBusinessContact
    ? { legalName: formValues?.businessName }
    : {
        firstName: formValues?.firstName,
        lastName: formValues?.lastName,
      }

  const request: CreateContactBankAccountRequest = {
    type: InstrumentType.BANK_ACCOUNT,
    currency,
    description,
    destinations: [transferDestination as BeneficiaryTransferDestination],
    accountNumber,
    accountHolderName,
    accountHolderAddress: {
      ...(beneficiaryAddressLine1 && {
        addressLine1: beneficiaryAddressLine1,
      }),
      ...(beneficiaryAddressLine2 && {
        addressLine2: beneficiaryAddressLine2,
      }),
      ...(beneficiaryRegion && { region: beneficiaryRegion }),
      ...(beneficiaryCity && { city: beneficiaryCity }),
      ...(beneficiaryCountryCode && { country: beneficiaryCountryCode }),
      ...(beneficiaryPostCode && { postCode: beneficiaryPostCode }),
    },
    bank: {
      ...bankDetails,
    },
    paymentMethods: getPaymentMethods(formValues),
    ...(intermediaryBankDetails && { intermediaryBanks: intermediaryBankDetails }),
  }

  return request
}

/**
 * Normalizes payment methods from API response to form values
 * The API returns ['SEPA_CT', 'SEPA_INST'] but the form only uses 'SEPA_CT' to represent both
 */
const normalizePaymentMethods = (
  paymentMethods: string[] | undefined,
  currency: string | undefined
): string[] => {
  if (!paymentMethods?.length) return []

  // For EUR, if SEPA_CT or SEPA_INST are present, normalize to just SEPA_CT
  if (currency === BeneficiaryDetailsCurrency.EUR) {
    const hasSepa =
      paymentMethods.includes(PaymentMethods.SEPA) ||
      paymentMethods.includes(PaymentMethods.SEPA_INSTANT)
    const hasSwift = paymentMethods.includes(PaymentMethods.SWIFT)

    return [...(hasSepa ? [PaymentMethods.SEPA] : []), ...(hasSwift ? [PaymentMethods.SWIFT] : [])]
  }

  return paymentMethods
}

/**
 * Maps a ContactInstrument (BANK_ACCOUNT type) to ContactFiatInstrumentFormData
 * Used for populating the form when editing an existing bank account instrument
 */
export const mapContactInstrumentToFiatInstrumentForm = (
  contactInstrument: ContactInstrument
): Partial<ContactFiatInstrumentFormData> => {
  const {
    currency,
    description,
    accountNumber,
    accountType,
    paymentMethods,
    transferDestination,
    bank,
    accountHolderName,
    accountHolderAddress,
    intermediaryBanks,
  } = contactInstrument || {}

  const normalizedPaymentMethods = normalizePaymentMethods(paymentMethods, currency)
  const isSwift = normalizedPaymentMethods?.includes(PaymentMethods.SWIFT)
  const isUSD = currency === BeneficiaryDetailsCurrency.USD

  const entityType = accountHolderName?.legalName
    ? BeneficiaryEntityType.COMPANY
    : BeneficiaryEntityType.INDIVIDUAL
  const isCompany = entityType === BeneficiaryEntityType.COMPANY
  const intermediaryBank = intermediaryBanks?.[0]

  const formData: Partial<ContactFiatInstrumentFormData> = {
    currency: currency || '',
    paymentMethods: normalizedPaymentMethods,
    transferDestination: transferDestination || '',
    entityType,
    description: description || '',
    ...(isCompany
      ? { legalName: accountHolderName?.legalName || '' }
      : { firstName: accountHolderName?.firstName, lastName: accountHolderName?.lastName }),
    ...(accountHolderAddress?.addressLine1 && {
      beneficiaryAddressLine1: accountHolderAddress.addressLine1,
    }),
    ...(accountHolderAddress?.addressLine2 && {
      beneficiaryAddressLine2: accountHolderAddress.addressLine2,
    }),
    ...(accountHolderAddress?.city && { beneficiaryCity: accountHolderAddress.city }),
    ...(accountHolderAddress?.region && { beneficiaryRegion: accountHolderAddress.region }),
    ...(accountHolderAddress?.postCode && { beneficiaryPostCode: accountHolderAddress.postCode }),
    ...(accountHolderAddress?.country && { beneficiaryCountryCode: accountHolderAddress.country }),
    // Bank account details
    accountNumber: accountNumber || '',
    ...(accountType && { accountType }),
    ...(bank?.swiftCode && { bankCode: bank.swiftCode }),
    ...(isUSD && bank?.identificationCode && { routingNumber: bank.identificationCode }),
    ...(bank?.name && { bankName: bank.name }),
    ...(bank?.address?.addressLine1 && { bankAddressLine1: bank.address.addressLine1 }),
    ...(bank?.address?.addressLine2 && { bankAddressLine2: bank.address.addressLine2 }),
    ...(bank?.address?.city && { bankCity: bank.address.city }),
    ...(bank?.address?.region && { bankRegion: bank.address.region }),
    ...(bank?.address?.postCode && { bankPostCode: bank.address.postCode }),
    ...(bank?.address?.country && { bankCountryCode: bank.address.country }),
  }

  // Add intermediary bank details if present
  if (intermediaryBank && isSwift) {
    return {
      ...formData,
      isIntermediaryBankDetailsRequired: true,
      ...(intermediaryBank.swiftCode && { intermediaryBankCode: intermediaryBank.swiftCode }),
      ...(isUSD &&
        intermediaryBank.identificationCode && {
          intermediaryBankRoutingNumber: intermediaryBank.identificationCode,
        }),
      ...(intermediaryBank.name && { intermediaryBankName: intermediaryBank.name }),
      ...(intermediaryBank.address?.addressLine1 && {
        intermediaryBankAddressLine1: intermediaryBank.address.addressLine1,
      }),
      ...(intermediaryBank.address?.addressLine2 && {
        intermediaryBankAddressLine2: intermediaryBank.address.addressLine2,
      }),
      ...(intermediaryBank.address?.city && {
        intermediaryBankCity: intermediaryBank.address.city,
      }),
      ...(intermediaryBank.address?.region && {
        intermediaryBankRegion: intermediaryBank.address.region,
      }),
      ...(intermediaryBank.address?.postCode && {
        intermediaryBankPostCode: intermediaryBank.address.postCode,
      }),
      ...(intermediaryBank.address?.country && {
        intermediaryBankCountryCode: intermediaryBank.address.country,
      }),
    }
  }

  return formData
}
